import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { ChevronLeft, ChevronRight, MapPin, Star } from 'lucide-react';
import { useData } from '../context/DataContext';
import { useCountry } from '../context/CountryContext';

const FeaturedClinics = () => {
  const navigate = useNavigate();
  const { clinics, specialties, countries, loading } = useData();
  const { detectedCountry } = useCountry();
  const [currentIndex, setCurrentIndex] = useState(0);
  
  if (loading || !clinics) {
    return (
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500 mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }
  
  // Filter featured clinics by country
  const featuredClinics = clinics.filter(clinic => 
    clinic.featured && (!detectedCountry || clinic.country === detectedCountry)
  );
  
  const countryInfo = countries?.find(c => c.code === detectedCountry);
  
  const itemsPerPage = 3;
  const maxIndex = Math.max(0, featuredClinics.length - itemsPerPage);

  const handleNext = () => {
    setCurrentIndex(prev => Math.min(prev + 1, maxIndex));
  };

  const handlePrev = () => {
    setCurrentIndex(prev => Math.max(prev - 1, 0));
  };

  const getSpecialtyName = (id) => {
    if (!specialties) return id;
    const specialty = specialties.find(s => s.id === id);
    return specialty ? specialty.name : id;
  };

  return (
    <div className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-4xl font-bold text-gray-900 mb-2">Clínicas Destacadas</h2>
            <p className="text-gray-600">
              Las mejores clínicas dentales {countryInfo ? `en ${countryInfo.flag} ${countryInfo.name}` : 'recomendadas por nuestros usuarios'}
            </p>
          </div>
          <Button 
            variant="outline" 
            className="border-cyan-500 text-cyan-600 hover:bg-cyan-50"
            onClick={() => navigate('/clinicas')}
          >
            Ver todas las clínicas
          </Button>
        </div>

        <div className="relative">
          <div className="overflow-hidden">
            <div 
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${currentIndex * (100 / itemsPerPage)}%)` }}
            >
              {featuredClinics.map((clinic) => (
                <div 
                  key={clinic.id} 
                  className="w-full md:w-1/2 lg:w-1/3 flex-shrink-0 px-3"
                  onClick={() => navigate(`/clinicas/${clinic.id}`)}
                >
                  <Card className="group hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden border-gray-200">
                    <div className="relative overflow-hidden h-48">
                      <img
                        src={clinic.image}
                        alt={clinic.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute top-3 right-3">
                        <div className="bg-white rounded-full p-2 shadow-md">
                          <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                        </div>
                      </div>
                    </div>
                    <CardContent className="p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-cyan-600 transition-colors">
                        {clinic.name}
                      </h3>
                      <div className="flex items-center text-gray-600 mb-3">
                        <MapPin className="h-4 w-4 mr-1" />
                        <span className="text-sm">{clinic.city}</span>
                        {clinic.branches && clinic.branches.length > 1 && (
                          <Badge variant="outline" className="ml-2 text-xs border-cyan-500 text-cyan-600">
                            {clinic.branches.length} sucursales
                          </Badge>
                        )}
                      </div>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                        {clinic.description}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {clinic.specialties.slice(0, 3).map((spec, idx) => (
                          <Badge key={idx} variant="secondary" className="bg-cyan-100 text-cyan-700 hover:bg-cyan-200">
                            {getSpecialtyName(spec)}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>

          {currentIndex > 0 && (
            <button
              onClick={handlePrev}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-white rounded-full p-3 shadow-lg hover:bg-cyan-50 transition-colors z-10"
            >
              <ChevronLeft className="h-6 w-6 text-gray-600" />
            </button>
          )}
          
          {currentIndex < maxIndex && (
            <button
              onClick={handleNext}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-white rounded-full p-3 shadow-lg hover:bg-cyan-50 transition-colors z-10"
            >
              <ChevronRight className="h-6 w-6 text-gray-600" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FeaturedClinics;
